import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Token storage: localStorage + in-memory for session persistence across page refresh.
// HttpOnly cookies won't work because the platform ingress forces Access-Control-Allow-Origin: *
// which conflicts with credentials:true, causing browsers to reject Set-Cookie headers.
const TOKEN_KEY = 'ft_auth_token';
let authToken = localStorage.getItem(TOKEN_KEY);

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};

export const clearAuthToken = () => {
  authToken = null;
  localStorage.removeItem(TOKEN_KEY);
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach auth token to every request
api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/me') && !url.includes('/auth/login') && !url.includes('/auth/register')) {
        clearAuthToken();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  requestAccess: (data) => api.post('/auth/request-access', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  sendOTP: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  resendOTP: (phone) => api.post('/auth/resend-otp', { phone }),
  googleSession: (sessionId) => api.post('/auth/google-session', { session_id: sessionId }),
};

// Account API
export const accountAPI = {
  deactivate: () => api.post('/account/deactivate'),
  transferClients: (targetEmail) => api.post('/account/transfer-clients', { target_trainer_email: targetEmail }),
  deleteAccount: (confirmation) => api.post('/account/delete', { confirmation }),
  getClientCount: () => api.get('/account/client-count'),
};

// Admin API
export const adminAPI = {
  getPendingUsers: () => api.get('/admin/pending-users'),
  approveUser: (userId) => api.post(`/admin/approve-user/${userId}`),
  rejectUser: (userId) => api.post(`/admin/reject-user/${userId}`),
  getAnalytics: () => api.get('/admin/analytics'),
  getTrainers: () => api.get('/admin/trainers'),
  getTrainerClients: (trainerId) => api.get(`/admin/trainers/${trainerId}/clients`),
  getTrainerProfile: (trainerId) => api.get(`/admin/trainers/${trainerId}/profile`),
  suspendTrainer: (trainerId) => api.post(`/admin/trainers/${trainerId}/suspend`),
  unsuspendTrainer: (trainerId) => api.post(`/admin/trainers/${trainerId}/unsuspend`),
  reactivateTrainer: (trainerId) => api.post(`/admin/trainers/${trainerId}/reactivate`),
  makeTrainerPro: (trainerId) => api.post(`/admin/trainers/${trainerId}/make-pro`),
  makeTrainerFree: (trainerId) => api.post(`/admin/trainers/${trainerId}/make-free`),
  sendWarning: (trainerId, message) => api.post(`/admin/trainers/${trainerId}/send-warning?message=${encodeURIComponent(message)}`),
  getFeedback: () => api.get('/admin/feedback'),
  getUnreadFeedbackCount: () => api.get('/admin/feedback/unread-count'),
  updateFeedbackStatus: (feedbackId, status) => api.put(`/admin/feedback/${feedbackId}/status?status=${status}`),
  cleanupTestUsers: () => api.post('/admin/cleanup-test-users'),
  wipeCoaches: () => api.post('/admin/wipe-coaches'),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  getCoachesSummary: () => api.get('/admin/coaches-summary'),
  getUserAnalytics: () => api.get('/admin/user-analytics'),
};

// Feedback API
export const feedbackAPI = {
  submit: (data) => api.post('/feedback', data),
};

// Payments API
export const paymentsAPI = {
  createOrder: (plan) => api.post('/payments/create-order', { plan }),
  verifyPayment: (data) => api.post('/payments/verify', data),
  getHistory: () => api.get('/payments/history'),
  getSummary: () => api.get('/payment-summary'),
};

// Client Payments API
export const clientPaymentsAPI = {
  record: (clientId, data) => api.post(`/clients/${clientId}/payments`, data),
  getAll: (clientId) => api.get(`/clients/${clientId}/payments`),
  remove: (clientId, paymentId) => api.delete(`/clients/${clientId}/payments/${paymentId}`),
};

// Diet Plans API
export const dietPlansAPI = {
  create: (clientId, data) => api.post(`/clients/${clientId}/diet-plans`, data),
  getAll: (clientId) => api.get(`/clients/${clientId}/diet-plans`),
  update: (clientId, planId, data) => api.put(`/clients/${clientId}/diet-plans/${planId}`, data),
  remove: (clientId, planId) => api.delete(`/clients/${clientId}/diet-plans/${planId}`),
};

// CSV Import API
export const csvImportAPI = {
  importFile: (formData) => api.post('/clients/import/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadTemplate: () => api.get('/clients/import/csv-template', { responseType: 'blob' }),
};

// Attendance API
export const attendanceAPI = {
  mark: (clientId, data) => api.post(`/clients/${clientId}/attendance`, data),
  getAll: (clientId) => api.get(`/clients/${clientId}/attendance`),
  remove: (clientId, date) => api.delete(`/clients/${clientId}/attendance/${date}`),
  getToday: () => api.get('/attendance/today'),
  getStats: (clientId) => api.get(`/clients/${clientId}/attendance/stats`),
  batchMark: (status, clientIds = null) => api.post('/attendance/batch', { status, client_ids: clientIds }),
};

// Notifications API
export const notificationsAPI = {
  registerToken: (token) => api.post('/notifications/register-token', { token }),
  unregisterToken: (token) => api.delete('/notifications/unregister-token', { data: { token } }),
  getStatus: () => api.get('/notifications/status'),
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAllRead: () => api.post('/notifications/mark-read'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
};

// Referral/Invite API
export const referralAPI = {
  getInviteLink: () => api.get('/trainer/invite-link'),
  getMyReferrals: () => api.get('/trainer/referrals'),
  getStats: () => api.get('/trainer/referral-stats'),
};

// Weekly Plans API
export const plansAPI = {
  create: (clientId, data) => api.post(`/clients/${clientId}/plans`, data),
  getAll: (clientId) => api.get(`/clients/${clientId}/plans`),
  getActive: (clientId) => api.get(`/clients/${clientId}/plans/active`),
  update: (clientId, planId, data) => api.put(`/clients/${clientId}/plans/${planId}`, data),
  duplicateWeek: (clientId, planId, sourceWeek, targetWeek) =>
    api.post(`/clients/${clientId}/plans/${planId}/duplicate-week?source_week=${sourceWeek}&target_week=${targetWeek}`),
  getHistory: (clientId, planId) => api.get(`/clients/${clientId}/plans/${planId}/history`),
};

// Client Magic Link API
export const clientAuthAPI = {
  generateMagicLink: (clientId) => api.post(`/clients/${clientId}/magic-link`),
  resolveShortLink: (code) => {
    const publicApi = axios.create({ baseURL: API_BASE });
    return publicApi.get(`/p/${code}`);
  },
  verifyToken: (token) => {
    const clientApi = axios.create({ baseURL: API_BASE });
    return clientApi.get('/client/auth/verify', { headers: { Authorization: `Bearer ${token}` } });
  },
  getDashboard: (token) => {
    const clientApi = axios.create({ baseURL: API_BASE });
    return clientApi.get('/client/dashboard', { headers: { Authorization: `Bearer ${token}` } });
  },
};

// Clients API
export const clientsAPI = {
  getAll: (includeArchived = false) => api.get(`/clients?include_archived=${includeArchived}`),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  archive: (id) => api.post(`/clients/${id}/archive`),
  unarchive: (id) => api.post(`/clients/${id}/unarchive`),
  getArchived: () => api.get('/clients/archived'),
  uploadPhoto: (id, formData) => api.post(`/clients/${id}/upload-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  changeGoal: (id, newGoal, createNewPlan = false) => api.post(`/clients/${id}/change-goal`, { new_goal: newGoal, create_new_plan: createNewPlan }),
  getGoalHistory: (id) => api.get(`/clients/${id}/goal-history`),
};

// Progress API
export const progressAPI = {
  getAll: (clientId) => api.get(`/clients/${clientId}/progress`),
  create: (clientId, data) => api.post(`/clients/${clientId}/progress`, data),
  delete: (clientId, entryId) => api.delete(`/clients/${clientId}/progress/${entryId}`),
};

// Workouts API
export const workoutsAPI = {
  getAll: (clientId) => api.get(`/clients/${clientId}/workouts`),
  create: (clientId, data) => api.post(`/clients/${clientId}/workouts`, data),
  delete: (clientId, entryId) => api.delete(`/clients/${clientId}/workouts/${entryId}`),
};

// Photos API
export const photosAPI = {
  getAll: (clientId) => api.get(`/clients/${clientId}/photos`),
  create: (clientId, data) => api.post(`/clients/${clientId}/photos`, data),
  delete: (clientId, photoId) => api.delete(`/clients/${clientId}/photos/${photoId}`),
};

// Notes API
export const notesAPI = {
  getAll: (clientId) => api.get(`/clients/${clientId}/notes`),
  create: (clientId, data) => api.post(`/clients/${clientId}/notes`, data),
  update: (clientId, noteId, data) => api.put(`/clients/${clientId}/notes/${noteId}`, data),
  delete: (clientId, noteId) => api.delete(`/clients/${clientId}/notes/${noteId}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getReminders: () => api.get('/dashboard/reminders'),
  triggerReminders: () => api.post('/dashboard/trigger-reminders'),
};

// Revenue API
export const revenueAPI = {
  getDashboard: () => api.get('/revenue/dashboard'),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
};

export default api;
